// src/lib/pwa/pushClient.js
import { supabase } from "../../supabaseClient";

export const NOTIFICATION_STATUS = Object.freeze({
  NOT_REQUESTED: "not_requested",
  PREFERENCE_DISABLED: "preference_disabled",
  GRANTED: "granted",
  DENIED: "denied",
  DISMISSED: "dismissed",
  UNSUPPORTED: "unsupported",
  INSTALLATION_REQUIRED: "installation_required",
  SUBSCRIPTION_FAILED: "subscription_failed",
});

const SERVICE_WORKER_URL = "/service-worker.js";
const DEVICE_ID_KEY = "vf_device_id";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);

  const base64 = (base64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let index = 0; index < rawData.length; index += 1) {
    outputArray[index] = rawData.charCodeAt(index);
  }

  return outputArray;
}

export function isIOSDevice() {
  if (typeof navigator === "undefined") return false;

  const userAgent = navigator.userAgent || "";

  return (
    /iPhone|iPad|iPod/i.test(userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

export function isStandalonePwa() {
  if (typeof window === "undefined") return false;

  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

export function getBrowserNotificationPermission() {
  if (typeof window === "undefined") return "unsupported";
  if (!("Notification" in window)) return "unsupported";

  return Notification.permission;
}

export function getNotificationEnvironment() {
  const notificationSupported =
    typeof window !== "undefined" && "Notification" in window;

  const serviceWorkerSupported =
    typeof navigator !== "undefined" && "serviceWorker" in navigator;

  const pushSupported =
    typeof window !== "undefined" && "PushManager" in window;

  const secureContext =
    typeof window !== "undefined" && window.isSecureContext === true;

  const ios = isIOSDevice();
  const standalone = isStandalonePwa();

  return {
    notificationSupported,
    serviceWorkerSupported,
    pushSupported,
    secureContext,
    ios,
    standalone,
    requiresIOSInstall: ios && !standalone,
    permission: getBrowserNotificationPermission(),
    supported:
      notificationSupported &&
      serviceWorkerSupported &&
      pushSupported &&
      secureContext,
  };
}

function getOrCreateDeviceId() {
  try {
    let deviceId = localStorage.getItem(DEVICE_ID_KEY);

    if (!deviceId) {
      deviceId =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `dev_${Math.random().toString(16).slice(2)}_${Date.now()}`;

      localStorage.setItem(DEVICE_ID_KEY, deviceId);
    }

    return deviceId;
  } catch {
    return `dev_${Math.random().toString(16).slice(2)}_${Date.now()}`;
  }
}

function getDeviceName() {
  const userAgent = navigator.userAgent || "Unknown";

  const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
  const isAndroid = /Android/i.test(userAgent);
  const isWindows = /Windows/i.test(userAgent);
  const isMac = /Macintosh/i.test(userAgent);
  const isLinux = /Linux/i.test(userAgent) && !isAndroid;

  let operatingSystem = "Device";

  if (isIOS) operatingSystem = "iOS";
  else if (isAndroid) operatingSystem = "Android";
  else if (isWindows) operatingSystem = "Windows";
  else if (isMac) operatingSystem = "macOS";
  else if (isLinux) operatingSystem = "Linux";

  let browser = "Browser";

  if (/Edg/i.test(userAgent)) browser = "Edge";
  else if (/OPR|Opera/i.test(userAgent)) browser = "Opera";
  else if (/Chrome/i.test(userAgent) && !/Edg/i.test(userAgent)) {
    browser = "Chrome";
  } else if (/Safari/i.test(userAgent) && !/Chrome/i.test(userAgent)) {
    browser = "Safari";
  } else if (/Firefox/i.test(userAgent)) {
    browser = "Firefox";
  }

  return `${operatingSystem} • ${browser}`;
}

async function getAuthContext() {
  let {
    data: { session },
    error,
  } = await supabase.auth.getSession();

  if (error) throw error;

  if (!session?.access_token) {
    const refreshResult = await supabase.auth.refreshSession();

    if (refreshResult.error) throw refreshResult.error;

    session = refreshResult.data?.session || null;
  }

  if (!session?.user || !session?.access_token) {
    throw new Error("You must be logged in to manage notifications.");
  }

  return {
    user: session.user,
    accessToken: session.access_token,
  };
}

async function updateNotificationState({ enabled, status }) {
  const { user } = await getAuthContext();

  const { error } = await supabase
    .from("user")
    .update({
      notifications_enabled: Boolean(enabled),
      notification_permission_status: status,
      notification_permission_updated_at: new Date().toISOString(),
    })
    .eq("id", user.id);

  if (error) throw error;

  return {
    userId: user.id,
    notificationsEnabled: Boolean(enabled),
    status,
  };
}

async function authenticatedFunctionRequest(path, body) {
  const { accessToken } = await getAuthContext();

  const response = await fetch(path, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
  });

  const text = await response.text();

  let payload = null;

  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    throw new Error(
      payload?.error ||
        text ||
        `Notification request failed with status ${response.status}.`
    );
  }

  return payload;
}

async function getServiceWorkerRegistration() {
  const environment = getNotificationEnvironment();

  if (!environment.secureContext) {
    throw new Error("Notifications require HTTPS.");
  }

  if (!environment.serviceWorkerSupported) {
    throw new Error("Service workers are not supported on this browser.");
  }

  await navigator.serviceWorker.register(SERVICE_WORKER_URL, {
    scope: "/",
  });

  return navigator.serviceWorker.ready;
}

async function getOrCreatePushSubscription() {
  const publicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;

  if (!publicKey) {
    throw new Error("Missing REACT_APP_VAPID_PUBLIC_KEY.");
  }

  const registration = await getServiceWorkerRegistration();

  let subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  return subscription;
}

async function saveSubscriptionToServer(subscription) {
  return authenticatedFunctionRequest(
    "/.netlify/functions/save-subscription",
    {
      subscription: subscription.toJSON(),
      userAgent: navigator.userAgent,
      deviceId: getOrCreateDeviceId(),
      deviceName: getDeviceName(),
    }
  );
}

export async function getCurrentDeviceSubscription() {
  const environment = getNotificationEnvironment();

  if (
    !environment.serviceWorkerSupported ||
    !environment.pushSupported
  ) {
    return null;
  }

  try {
    const registration = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise((_, reject) => {
        window.setTimeout(() => {
          reject(new Error("Service worker readiness timed out."));
        }, 2500);
      }),
    ]);

    return registration.pushManager.getSubscription();
  } catch {
    return null;
  }
}

export async function getNotificationSnapshot() {
  const environment = getNotificationEnvironment();
  const subscription = await getCurrentDeviceSubscription();

  let notificationsEnabled = false;
  let storedStatus = NOTIFICATION_STATUS.NOT_REQUESTED;

  try {
    const { user } = await getAuthContext();

    const { data, error } = await supabase
      .from("user")
      .select(
        "notifications_enabled, notification_permission_status, notification_permission_updated_at"
      )
      .eq("id", user.id)
      .maybeSingle();

    if (error) throw error;

    notificationsEnabled = data?.notifications_enabled === true;
    storedStatus =
      data?.notification_permission_status ||
      NOTIFICATION_STATUS.NOT_REQUESTED;
  } catch {
    // The browser state can still be displayed if the database read fails.
  }

  return {
    ...environment,
    notificationsEnabled,
    storedStatus,
    deviceSubscribed: Boolean(subscription),
    subscription,
  };
}


export async function requestAndEnablePushNotifications() {
  const environment = getNotificationEnvironment();

  // Check iOS installation first
  if (environment.requiresIOSInstall) {
    await updateNotificationState({
      enabled: false,
      status: NOTIFICATION_STATUS.INSTALLATION_REQUIRED,
    });

    return {
      ok: false,
      enabled: false,
      status: NOTIFICATION_STATUS.INSTALLATION_REQUIRED,
      permission: environment.permission,
      message:
        "Please add Gwin to your Home Screen and open it from the app icon before enabling notifications.",
    };
  }

  // Check browser support
  if (!environment.supported) {
    await updateNotificationState({
      enabled: false,
      status: NOTIFICATION_STATUS.UNSUPPORTED,
    });

    return {
      ok: false,
      enabled: false,
      status: NOTIFICATION_STATUS.UNSUPPORTED,
      permission: environment.permission,
      message:
        "Push notifications are not supported on this browser or device.",
    };
  }

  // Check current permission
  let permission = environment.permission;

  if (permission === "denied") {
    await updateNotificationState({
      enabled: false,
      status: NOTIFICATION_STATUS.DENIED,
    });

    return {
      ok: false,
      enabled: false,
      status: NOTIFICATION_STATUS.DENIED,
      permission,
      message:
        "Notifications are blocked in your browser settings. Change the permission to Allow, refresh Gwin, and try again.",
    };
  }

  // Request permission directly from the user's action
  if (permission === "default") {
    permission = await Notification.requestPermission();
  }

  if (permission !== "granted") {
    const status =
      permission === "denied"
        ? NOTIFICATION_STATUS.DENIED
        : NOTIFICATION_STATUS.DISMISSED;

    await updateNotificationState({
      enabled: false,
      status,
    });

    return {
      ok: false,
      enabled: false,
      status,
      permission,
      message:
        status === NOTIFICATION_STATUS.DENIED
          ? "Notifications were blocked. You can enable them later from your browser settings and Gwin Profile."
          : "The notification permission prompt was closed. You can enable notifications later from your Gwin Profile.",
    };
  }

  try {
    const subscription = await getOrCreatePushSubscription();

    await saveSubscriptionToServer(subscription);

    await updateNotificationState({
      enabled: true,
      status: NOTIFICATION_STATUS.GRANTED,
    });

    return {
      ok: true,
      enabled: true,
      status: NOTIFICATION_STATUS.GRANTED,
      permission,
      subscription,
      message: "Notifications enabled. This device is now registered.",
    };
  } catch (error) {
    await updateNotificationState({
      enabled: false,
      status: NOTIFICATION_STATUS.SUBSCRIPTION_FAILED,
    });

    return {
      ok: false,
      enabled: false,
      status: NOTIFICATION_STATUS.SUBSCRIPTION_FAILED,
      permission,
      error,
      message:
        error?.message ||
        "Notification permission was granted, but this device could not be registered.",
    };
  }
}

export async function restorePushSubscriptionIfAllowed({
  allowPreferenceEnable = false,
} = {}) {
  const environment = getNotificationEnvironment();

  if (!environment.supported) {
    return {
      ok: false,
      status: NOTIFICATION_STATUS.UNSUPPORTED,
      message: "Push notifications are not supported.",
    };
  }

  if (environment.requiresIOSInstall) {
    return {
      ok: false,
      status: NOTIFICATION_STATUS.INSTALLATION_REQUIRED,
      message: "Gwin must be installed on the Home Screen first.",
    };
  }

  const { user } = await getAuthContext();

  const { data, error } = await supabase
    .from("user")
    .select("notifications_enabled")
    .eq("id", user.id)
    .maybeSingle();

  if (error) throw error;

  const preferenceEnabled = data?.notifications_enabled === true;

  if (!preferenceEnabled && !allowPreferenceEnable) {
    return {
      ok: false,
      status: NOTIFICATION_STATUS.PREFERENCE_DISABLED,
      message: "The user has not enabled notifications.",
    };
  }

  if (environment.permission !== "granted") {
    if (environment.permission === "denied") {
      await updateNotificationState({
        enabled: false,
        status: NOTIFICATION_STATUS.DENIED,
      });
    }

    return {
      ok: false,
      status:
        environment.permission === "denied"
          ? NOTIFICATION_STATUS.DENIED
          : NOTIFICATION_STATUS.NOT_REQUESTED,
      permission: environment.permission,
      message: "Browser permission has not been granted.",
    };
  }

  try {
    const subscription = await getOrCreatePushSubscription();

    await saveSubscriptionToServer(subscription);

    await updateNotificationState({
      enabled: true,
      status: NOTIFICATION_STATUS.GRANTED,
    });

    return {
      ok: true,
      status: NOTIFICATION_STATUS.GRANTED,
      permission: "granted",
      subscription,
      message: "This device is registered for notifications.",
    };
  } catch (error) {
    await updateNotificationState({
      enabled: false,
      status: NOTIFICATION_STATUS.SUBSCRIPTION_FAILED,
    });

    return {
      ok: false,
      status: NOTIFICATION_STATUS.SUBSCRIPTION_FAILED,
      permission: environment.permission,
      error,
      message:
        error?.message || "Could not restore the notification subscription.",
    };
  }
}

export async function disablePushNotifications({
  allDevices = true,
} = {}) {
  await updateNotificationState({
    enabled: false,
    status: NOTIFICATION_STATUS.PREFERENCE_DISABLED,
  });

  let endpoint = null;
  let localUnsubscribed = false;

  try {
    const subscription = await getCurrentDeviceSubscription();

    if (subscription) {
      endpoint = subscription.endpoint;
      localUnsubscribed = await subscription.unsubscribe();
    }
  } catch {
    // The database preference is already disabled.
  }

  let serverError = null;

  try {
    await authenticatedFunctionRequest(
      "/.netlify/functions/delete-subscription",
      {
        endpoint,
        deviceId: getOrCreateDeviceId(),
        allDevices,
      }
    );
  } catch (error) {
    serverError = error;
  }

  return {
    ok: !serverError,
    enabled: false,
    status: NOTIFICATION_STATUS.PREFERENCE_DISABLED,
    localUnsubscribed,
    serverError,
    message: serverError
      ? "Notifications were turned off, but an old device subscription could not be removed from the server."
      : allDevices
        ? "Notifications were turned off on all registered devices."
        : "Notifications were turned off on this device.",
  };
}